import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { validateExistingCustomer } from '@/api/auth';

import { ERROR_CODE } from '@/constants/api';
import { LS_HAS_LANGUAGE_SCREEN } from '@/constants/common';
import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import i18n from '@/i18n/i18n';

import { selectTerritoriesData } from '@/redux/countries/selectors';

import { Tenant } from '@/types/common';
import { OperationType } from '@/types/user';

import { getAPIErrorCode } from '@/utils/api';
import { removeLocal, saveLocal } from '@/utils/browser';
import { saveOperationType } from '@/utils/operationType';

import { CountrySelectionForm } from '../types/form';

type UseEXCRequestOTPProps = {
  getValues: () => CountrySelectionForm;
  excTenant?: string;
};

export const useEXCRequestOTP = ({ getValues }: UseEXCRequestOTPProps) => {
  const navigate = useNavigate();
  const territories = useAppSelector(selectTerritoriesData);
  const { switchTenant } = useTranslate();
  const { mutate: requestExcOtp, isPending: isValidatingExc } = useMutation({
    mutationFn: validateExistingCustomer,
    onSuccess: async response => {
      saveOperationType(OperationType.Existing);
      const { tenant, email, language } = response;

      const countryCode =
        territories.find(item => item.tenant === tenant.toLowerCase())
          ?.countryCode || '';

      const updatedForm = {
        ...getValues(),
        email: email,
        countryCode,
      };

      if (updatedForm.email) {
        if (tenant === Tenant.Suriname && !language) {
          saveLocal(LS_HAS_LANGUAGE_SCREEN, true);
          navigate(ROUTE.LANGUAGE_SELECTION, {
            state: {
              from: ROUTE.LOGIN,
              form: getValues(),
              type: OperationType.Existing,
              tenant: tenant,
            },
          });
          return;
        }
        if (language) {
          switchTenant(tenant.toLowerCase() as Tenant);
          i18n.changeLanguage(language);
        }
        navigate(ROUTE.LOGIN_VERIFY_EMAIL, {
          state: {
            from: ROUTE.LOGIN,
            form: updatedForm,
            type: OperationType.Existing,
            tenant: tenant,
          },
        });
      }
    },
    onError: error => {
      const errCode = getAPIErrorCode(error);
      if (errCode === ERROR_CODE.EXC_PRE_APPROVED_STATUS_IS_NOT_ALLOWED) {
        navigate(ROUTE.LOGIN_PRE_APPROVED_STATUS);
      }
    },
  });

  useEffect(() => {
    saveOperationType('');
    removeLocal(LS_HAS_LANGUAGE_SCREEN);
  }, []);

  return {
    requestExcOtp,
    isValidatingExc,
  };
};
